/** The day in numbers and seen from above (Sprint 103.0, mockup 6). */

import type { ReactNode } from 'react';
import { compassPoint, type SolarEvent } from '@archisimple/solar';
import { degrees } from '../model/format.ts';
import type { SkyDirection } from '../model/projection.ts';
import type { Day } from '../model/sky.ts';
import { BackButton, Icon, LanguageSwitch, useWords } from './common.tsx';

const CX = 150;
const CY = 118;
const R = 108;

/** Azimuth round the circle with north up, altitude inwards from the rim. */
function polar(direction: SkyDirection): [number, number] {
  const r = (R * (90 - Math.max(direction.altitudeDegrees, 0))) / 90;
  const a = (direction.azimuthDegrees * Math.PI) / 180;
  return [CX + r * Math.sin(a), CY - r * Math.cos(a)];
}

function points(path: readonly SkyDirection[]): string {
  return path
    .map((d) =>
      polar(d)
        .map((n) => n.toFixed(1))
        .join(',')
    )
    .join(' ');
}

export function Summary({
  title,
  day,
  references,
  sun,
  change,
  zoneLine,
  onBack
}: {
  readonly title: string;
  readonly day: Day;
  readonly references: readonly (readonly SkyDirection[])[];
  readonly sun: SkyDirection & { readonly aboveHorizon: boolean };
  readonly change: number;
  readonly zoneLine: string;
  readonly onBack: () => void;
}): ReactNode {
  const { t, language } = useWords();
  const { solar } = day;
  const first = solar.path[0];
  const last = solar.path.at(-1);
  const event = (
    e: SolarEvent,
    at: SkyDirection | undefined
  ): { value: string; detail?: string } =>
    e.kind === 'at'
      ? {
          value: e.localTime,
          ...(at === undefined
            ? {}
            : {
                detail: `${degrees(at.azimuthDegrees, language)}° · ${t(`point.${compassPoint(at.azimuthDegrees)}`)}`
              })
        }
      : { value: t(e.reason === 'polar-day' ? 'summary.polarDay' : 'summary.polarNight') };
  const rise = event(solar.sunrise, first);
  const set = event(solar.sunset, last);
  const hours = Math.floor(solar.dayLengthMinutes / 60);
  const minutes = solar.dayLengthMinutes % 60;
  const changeWords =
    change === 0
      ? t('summary.same')
      : t(change > 0 ? 'summary.longer' : 'summary.shorter', { minutes: Math.abs(change) });
  const [sx, sy] = polar(sun);

  return (
    <main className="screen">
      <div className="row">
        <BackButton onClick={onBack} />
        <div className="spacer" />
        <LanguageSwitch />
      </div>
      <div>
        <h1 className="title" style={{ marginTop: 0 }}>
          {t('summary.title')}
        </h1>
        <p className="subtitle">{title}</p>
      </div>
      <div className="card diagram">
        <svg viewBox="0 0 300 240" role="img" aria-label={t('summary.diagram')}>
          <circle
            cx={CX}
            cy={CY}
            r={R}
            fill="var(--color-bg-selected)"
            stroke="var(--color-border-strong)"
          />
          <circle
            cx={CX}
            cy={CY}
            r={(R * 2) / 3}
            fill="none"
            stroke="var(--color-border-strong)"
            strokeDasharray="3 4"
          />
          <circle
            cx={CX}
            cy={CY}
            r={R / 3}
            fill="none"
            stroke="var(--color-border-strong)"
            strokeDasharray="3 4"
          />
          <path
            d={`M${CX} ${CY - R} V${CY + R} M${CX - R} ${CY} H${CX + R}`}
            stroke="var(--color-border)"
          />
          <g fontSize="11" fontWeight="700" fill="var(--color-text-subtle)">
            <text x={CX} y={CY - R - 3} textAnchor="middle">
              {t('cardinal.n')}
            </text>
            <text x={CX} y={CY + R + 13} textAnchor="middle">
              {t('cardinal.s')}
            </text>
            <text x={CX + R + 6} y={CY + 4}>
              {t('cardinal.e')}
            </text>
            <text x={CX - R - 6} y={CY + 4} textAnchor="end">
              {t('cardinal.w')}
            </text>
          </g>
          <g fontSize="10" fill="var(--color-text-muted)">
            <text x={CX + 3} y={CY - (R * 2) / 3 + 12}>
              30°
            </text>
            <text x={CX + 3} y={CY - R / 3 + 12}>
              60°
            </text>
          </g>
          {references.map((path, index) => (
            <polyline
              key={index}
              points={points(path)}
              fill="none"
              stroke="var(--color-text-muted)"
              strokeWidth="1.5"
              strokeDasharray="2 4"
              strokeLinecap="round"
            />
          ))}
          <polyline
            points={points(solar.path)}
            fill="none"
            stroke="var(--color-sun-strong)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {sun.aboveHorizon ? (
            <circle
              cx={sx}
              cy={sy}
              r="8"
              fill="var(--color-sun)"
              stroke="var(--color-bg-surface)"
              strokeWidth="2.5"
            />
          ) : null}
        </svg>
        <div className="legend">
          <span>
            <svg width="18" height="6" aria-hidden="true">
              <path
                d="M1 3H17"
                stroke="var(--color-sun-strong)"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
            {t('summary.today')}
          </span>
          <span>
            <svg width="18" height="6" aria-hidden="true">
              <path
                d="M1 3H17"
                stroke="var(--color-text-muted)"
                strokeWidth="1.5"
                strokeDasharray="2 4"
                strokeLinecap="round"
              />
            </svg>
            {t('summary.references')}
          </span>
        </div>
      </div>
      <dl className="card facts">
        <div>
          <dt>
            <Icon name="sunrise" />
            {t('summary.sunrise')}
          </dt>
          <dd>
            <strong>{rise.value}</strong>
            {rise.detail === undefined ? null : <span>{rise.detail}</span>}
          </dd>
        </div>
        <div>
          <dt>
            <Icon name="noon" />
            {t('summary.noon')}
          </dt>
          <dd>
            <strong>{solar.solarNoon.localTime}</strong>
            <span>
              {t('ar.above', { degrees: degrees(solar.solarNoon.altitudeDegrees, language, 1) })}
            </span>
          </dd>
        </div>
        <div>
          <dt>
            <Icon name="sunset" />
            {t('summary.sunset')}
          </dt>
          <dd>
            <strong>{set.value}</strong>
            {set.detail === undefined ? null : <span>{set.detail}</span>}
          </dd>
        </div>
        <div>
          <dt>
            <Icon name="clock" />
            {t('summary.length')}
          </dt>
          <dd>
            <strong>{t('summary.lengthValue', { hours, minutes })}</strong>
            <span>{changeWords}</span>
          </dd>
        </div>
      </dl>
      <p className="note">
        {zoneLine} {t('summary.north')}
      </p>
      <div className="spacer" />
      <button type="button" className="btn btn--primary btn--block" onClick={onBack}>
        {t('summary.back')}
      </button>
    </main>
  );
}
